import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useQueryClient } from "react-query";
import axios from "axios";
import { API_ENDPOINT } from "../../constants/constants";
import { getHeader } from "../../utils/util";
import { dateKey } from "../../utils/listData";
import useApiEp from "../hooks/useApiEp";
import { invalidateTools, money, orderTotal } from "./toolsData";
import { ToolsState } from "./ToolsLayout";
import ReceiptPreview from "./ReceiptPreview";

export function validatePayment(status, transactionId, paymentDate, today = dateKey(new Date())) {
  if (!["paid", "processing", "rejected", "pending"].includes(status))
    return "Choose a payment status.";
  if (status === "paid") {
    if (!transactionId.trim()) return "Enter the transaction ID from the payment record.";
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(paymentDate) ||
      Number.isNaN(new Date(paymentDate).getTime()) ||
      new Date(paymentDate).toISOString().slice(0, 10) !== paymentDate
    )
      return "Enter a valid payment date.";
    if (paymentDate > today) return "Payment date cannot be in the future.";
  }
  return "";
}

export default function PaymentReviewDialog({ orderId, onClose, onSaved }) {
  const query = useApiEp(`admin/get-tools-order/${encodeURIComponent(orderId)}`);
  const client = useQueryClient();
  const [status, setStatus] = useState("paid"),
    [transactionId, setTransactionId] = useState(""),
    [paymentDate, setPaymentDate] = useState(""),
    [saving, setSaving] = useState(false),
    [error, setError] = useState("");
  const order = query.data;
  const final = ["paid", "rejected"].includes(order?.paymentStatus);
  const submit = async (event) => {
    event.preventDefault();
    if (saving || !order || final) return;
    const issue = validatePayment(status, transactionId, paymentDate);
    if (issue) {
      setError(issue);
      return;
    }
    setError("");
    setSaving(true);
    try {
      const fresh = await query.refetch();
      if (fresh.isError || !fresh.data)
        throw new Error("Unable to verify the current order. Please retry.");
      if (
        fresh.data.paymentStatus !== order.paymentStatus ||
        fresh.data.receiptUrl !== order.receiptUrl ||
        orderTotal(fresh.data) !== orderTotal(order)
      )
        throw new Error(
          "Payment details changed while you were reviewing. Close this dialog and review the updated order and receipt.",
        );
      await axios.post(
        `${API_ENDPOINT}admin/update-order-payment-status/${encodeURIComponent(orderId)}`,
        { status, ...(status === "paid" && { transactionId: transactionId.trim(), paymentDate }) },
        getHeader(),
      );
      await invalidateTools(client);
      setSaving(false);
      onSaved(
        status === "paid" ? `Payment for ${orderId} approved.` : `Payment for ${orderId} updated.`,
      );
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Unable to update payment. Please try again.",
      );
      setSaving(false);
    }
  };
  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <Box component="form" onSubmit={submit}>
        <DialogTitle>Review payment · {orderId}</DialogTitle>
        <DialogContent>
          <ToolsState
            loading={query.isLoading}
            error={query.isError}
            retry={query.refetch}
            empty={!order}
            emptyTitle="Order unavailable"
          >
            <div className="tools-two-column">
              <div>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Payment evidence
                </Typography>
                <ReceiptPreview key={order?.receiptUrl} url={order?.receiptUrl} />
              </div>
              <div>
                <Typography variant="subtitle2">Order total</Typography>
                <Typography variant="h5" sx={{ mb: 1 }}>
                  {money(orderTotal(order))}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {order?.shippingAddress?.fullName || "Customer unavailable"} ·{" "}
                  {order?.paymentMethod || "Payment method unspecified"}
                </Typography>
                {final ? (
                  <Alert severity="info">
                    This payment is already {order?.paymentStatus}. Transaction reference:{" "}
                    {order?.transactionId || "Unavailable"}.
                  </Alert>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      select
                      label="Review decision"
                      value={status}
                      onChange={(event) => {
                        setStatus(event.target.value);
                        setError("");
                      }}
                      disabled={saving}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value="paid">Approve payment</MenuItem>
                      <MenuItem value="processing">Keep under review</MenuItem>
                      <MenuItem value="rejected">Reject payment</MenuItem>
                      <MenuItem value="pending">Awaiting payment</MenuItem>
                    </TextField>
                    {status === "paid" && (
                      <>
                        <TextField
                          fullWidth
                          required
                          label="Transaction ID"
                          value={transactionId}
                          onChange={(event) => setTransactionId(event.target.value)}
                          disabled={saving}
                          sx={{ mb: 2 }}
                        />
                        <TextField
                          fullWidth
                          required
                          type="date"
                          label="Payment date"
                          value={paymentDate}
                          onChange={(event) => setPaymentDate(event.target.value)}
                          disabled={saving}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ max: dateKey(new Date()) }}
                          helperText="Use the date the payment was received."
                          sx={{ mb: 2 }}
                        />
                        <Alert severity="info">
                          Verify the receipt and payment record before approving this order's
                          payment.
                        </Alert>
                      </>
                    )}
                    {status === "rejected" && (
                      <Alert severity="warning">
                        Confirm the receipt does not match a valid payment before rejecting it.
                      </Alert>
                    )}
                  </>
                )}
              </div>
            </div>
          </ToolsState>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving}>
            {final ? "Close" : "Cancel"}
          </Button>
          {!final && (
            <LoadingButton
              type="submit"
              variant="contained"
              loading={saving}
              disabled={!order || query.isLoading || query.isError}
              color={status === "rejected" ? "error" : "primary"}
            >
              {status === "paid"
                ? "Approve payment"
                : status === "rejected"
                  ? "Reject payment"
                  : "Save decision"}
            </LoadingButton>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
}
